import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import tokens from '../../assets/styles/tokens';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';

const AvailableBalanceCardContainer = styled.div`
  border-radius: ${variables.borderRadius.borderRadiusMdPlus};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: 0px 3px 9px 0px ${themeColors.colorShadowCard};
  border: 1px solid ${themeColors.colorWhite};
  padding: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(16)} ${pxToRemMd(16)} ${pxToRemMd(12)};
  }
`;

const AvailableBalanceHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(10)};
  padding-bottom: ${pxToRem(12.5)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  margin-bottom: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding-bottom: ${pxToRemMd(12)};
    margin-bottom: ${pxToRemMd(24)};
    flex-wrap: wrap;
  }
`;

const AvailableBalanceItem = styled.div`
  & + & {
    margin-top: ${pxToRem(24)};
  }
`;

const AvailableBalanceItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(10)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    flex-wrap: wrap;
  }
`;

const AvailableBalanceTitle = styled.p`
  ${tokens.type.label[400]}
  color: ${themeColors.colorTextSecondary};
`;

const AvailableBalanceItemTitle = styled(AvailableBalanceTitle)`
  ${tokens.type.label[300]}
`;

const AvailableBalanceAmount = styled.p`
  ${tokens.type.title[400]}
  text-align: right;
  color: ${({ isPrimary }) =>
    isPrimary ? themeColors.colorTextPrimary : themeColors.colorTextSecondary};
`;

const AvailableBalanceItemAmount = styled(AvailableBalanceAmount)`
  ${tokens.type.label[300]}
`;

const AvailableBalanceDescription = styled.p`
  ${tokens.type.body[200]}
  color: ${themeColors.colorTextSecondary};
  margin: ${pxToRem(4)} 0 0;
`;

const AvailableBalanceCard = ({
  title,
  balance,
  tradingTitle,
  tradingBalance,
  tradingDescription,
  withdrawalTitle,
  withdrawalBalance,
  withdrawalDescription,
}) => (
  <AvailableBalanceCardContainer>
    <AvailableBalanceHeaderRow>
      <AvailableBalanceTitle>{title}</AvailableBalanceTitle>
      <AvailableBalanceAmount isPrimary>
        {formatMoneyNumeral(balance)}
      </AvailableBalanceAmount>
    </AvailableBalanceHeaderRow>
    <AvailableBalanceItem>
      <AvailableBalanceItemRow>
        <AvailableBalanceItemTitle>{tradingTitle}</AvailableBalanceItemTitle>
        <AvailableBalanceItemAmount>
          {formatMoneyNumeral(tradingBalance)}
        </AvailableBalanceItemAmount>
      </AvailableBalanceItemRow>
      <AvailableBalanceDescription>
        {tradingDescription}
      </AvailableBalanceDescription>
    </AvailableBalanceItem>
    <AvailableBalanceItem>
      <AvailableBalanceItemRow>
        <AvailableBalanceItemTitle>{withdrawalTitle}</AvailableBalanceItemTitle>
        <AvailableBalanceItemAmount>
          {formatMoneyNumeral(withdrawalBalance)}
        </AvailableBalanceItemAmount>
      </AvailableBalanceItemRow>
      <AvailableBalanceDescription>
        {withdrawalDescription}
      </AvailableBalanceDescription>
    </AvailableBalanceItem>
  </AvailableBalanceCardContainer>
);

AvailableBalanceCard.propTypes = {
  title: PropTypes.string,
  balance: PropTypes.number,
  tradingTitle: PropTypes.string,
  tradingBalance: PropTypes.number,
  tradingDescription: PropTypes.string,
  withdrawalTitle: PropTypes.string,
  withdrawalBalance: PropTypes.number,
  withdrawalDescription: PropTypes.string,
};

export default AvailableBalanceCard;
